import 'regenerator-runtime/runtime'

import 'client/styles/main.global.css'
import 'client/styles/texts.global.css'
import 'client/styles/stripe.global.css'
import 'client/styles/typography.global.css'

import { useEffect } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { Router, useLocation } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import getPageRoute from 'routes/ssr/get-page-route'
import App from 'routes/ssr/App'
import createStore from 'client/lib/create-store'
import { setPageError } from 'client/store/ui/actions'
import isModernBrowser from 'client/lib/is-modern-browser'
import ensureSafeMutationObserver from 'client/lib/ensure-safe-mutation-observer'
import { init as initSentry } from 'client/lib/sentry'
import { Globals as ReactSpringGlobals } from 'react-spring'

// Initialize Sentry as early as possible
initSentry()

// Accessibility-audit hook (no effect in normal use): when the a11y scanner sets this
// flag before the document loads, make react-spring jump every animation straight to its
// final value. The app fades content in (opacity 0→1) via react-spring, and the audit
// otherwise samples a mid-fade frame — reading text as a lightened composite over the page
// background and reporting false-positive color-contrast failures. Skipping the animation
// makes the scanned render deterministic.
if (typeof window !== 'undefined' && window.__A11Y_DISABLE_MOTION__) {
  ReactSpringGlobals.assign({ skipAnimation: true })
}

// Handle unhandled promise rejections from third-party scripts (e.g., UserWay widget)
window.addEventListener('unhandledrejection', event => {
  // Check if the error originates from UserWay widget
  const error = event.reason
  const stack = error?.stack || ''

  if (stack.includes('cdn.userway.org') || stack.includes('userway')) {
    // Suppress UserWay widget errors
    event.preventDefault()
    console.warn('Suppressed unhandled rejection from UserWay widget:', error)
  }
})
;(function () {
  const _error = console.error.bind(console)

  console.error = function (...args) {
    if (args?.[0]?.isAxiosError) {
      console.error(args[0].toJSON())
      return
    }

    _error(...args)
  }
})()

export default function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

export const AppRouter = ({ history, store }) => (
  <Router history={history}>
    <ScrollToTop />
    <App store={store} />
  </Router>
)

const loadPolyfills = async () => {
  if ('ResizeObserver' in window === false) {
    const module = await import('@juggle/resize-observer')
    window.ResizeObserver = module.ResizeObserver
  }
  ensureSafeMutationObserver()
  const isModern = isModernBrowser(navigator.userAgent)

  if (!isModern) {
    await import('core-js/stable')
  }
}

const main = async () => {
  await loadPolyfills()

  const history = createBrowserHistory()
  const { page } = await getPageRoute(history.location.pathname)
  let resolvedPage = page
  if (typeof page._payload?._result === 'function') {
    resolvedPage = await page._payload._result()
  } else if (page._payload) {
    resolvedPage = page._payload._result
  }
  const initialAsyncReducers = resolvedPage.default?.asyncStores || {}

  const initialStoreState = window.__SCITE_STORE_STATE
  const { store } = createStore(initialStoreState, history, initialAsyncReducers)
  history.listen((location, action) => {
    setTimeout(() => {
      const state = store.getState()
      if (state.ui.pageError) {
        store.dispatch(setPageError())
      }
    })
  })

  hydrateRoot(document.getElementById('scite-app'), <AppRouter history={history} store={store} />)
}

if (!CONFIG.maintenance) {
  window.addEventListener('load', main)
}
